import { action, makeObservable, observable } from "mobx"

import type { PendingClassicLpCreate, PendingCreation, PendingGaugeCreate } from "./types"

const STALE_MS = 10 * 60 * 1000 // 10 minutes

/**
 * In-memory store for pending Classic LP and Gauge creations.
 * Written after a successful create tx; read by holding pages to show "creating" state and poll for completion.
 * Cleared when the holding page finds the entity in the API or on timeout.
 */
export class PendingCreationStore {
  private _pendingClassicLp: PendingClassicLpCreate | null = null
  private _pendingGauge: PendingGaugeCreate | null = null

  constructor() {
    makeObservable(this, {
      _pendingClassicLp: observable,
      _pendingGauge: observable,
      setPendingClassicLp: action,
      setPendingGauge: action,
      clearPendingClassicLp: action,
      clearPendingGauge: action,
      clearStale: action,
    })
  }

  get pendingClassicLp(): PendingClassicLpCreate | null {
    return this._pendingClassicLp
  }

  get pendingGauge(): PendingGaugeCreate | null {
    return this._pendingGauge
  }

  setPendingClassicLp(data: Omit<PendingClassicLpCreate, "kind" | "createdAt">): void {
    this._pendingClassicLp = {
      kind: "classic-lp",
      ...data,
      createdAt: Date.now(),
    }
  }

  setPendingGauge(data: Omit<PendingGaugeCreate, "kind" | "createdAt">): void {
    this._pendingGauge = {
      kind: "gauge",
      ...data,
      createdAt: Date.now(),
    }
  }

  clearPendingClassicLp(): void {
    this._pendingClassicLp = null
  }

  clearPendingGauge(): void {
    this._pendingGauge = null
  }

  /**
   * Clear any pending creation older than STALE_MS (e.g. on app load).
   */
  clearStale(): void {
    const now = Date.now()
    if (this._pendingClassicLp && now - this._pendingClassicLp.createdAt > STALE_MS) {
      this._pendingClassicLp = null
    }
    if (this._pendingGauge && now - this._pendingGauge.createdAt > STALE_MS) {
      this._pendingGauge = null
    }
  }

  getPendingCreation(): PendingCreation | null {
    return this._pendingClassicLp ?? this._pendingGauge ?? null
  }
}
